import React, { useState } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { Controller } from 'react-hook-form'
import TextField from '@material-ui/core/TextField'
import { useForkRef } from '@material-ui/core/utils'
import Autocomplete from '@material-ui/lab/Autocomplete'
import emailSpellChecker from '@zootools/email-spell-checker'

// Email input with "did you mean ...@gmail.com?" suggestions, built like
// `components/form-util/email-field-with-suggestions.js`. That shared field
// spreads its props over the Autocomplete's own `inputProps`, which leaves the
// input with `autocomplete="off"`, a generated id and no name, so browsers and
// password managers could not autofill it. This copy keeps the Autocomplete's
// props and layers the autofill attributes on top.
const EmailInput = ({
  params,
  inputRef,
  name,
  label,
  error,
  helperText,
  autoComplete,
}) => {
  const forkedRef = useForkRef(params.inputProps.ref, inputRef)
  return (
    <TextField
      {...params}
      label={label}
      error={error}
      helperText={helperText}
      inputProps={{
        ...params.inputProps,
        ref: forkedRef,
        name,
        type: 'email',
        inputMode: 'email',
        autoComplete,
        autoCapitalize: 'none',
        spellCheck: false,
        'aria-required': true,
      }}
    />
  )
}

EmailInput.propTypes = {
  params: PropTypes.object.isRequired,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  autoComplete: PropTypes.string,
}

const EmailField = ({
  control,
  name,
  errors,
  label,
  id,
  autoComplete = 'email',
}) => {
  const [suggestion, setSuggestion] = useState('')

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      render={({ value, onChange, ref }) => (
        <Autocomplete
          id={id}
          disableClearable
          freeSolo
          value={value}
          options={suggestion ? [suggestion] : []}
          filterOptions={options => options}
          onChange={(event, next) => onChange(next)}
          onInputChange={(event, next, reason) => {
            // `input` is typing; `reset` and `clear` come from selecting an option.
            if (reason !== 'input') return
            setSuggestion(emailSpellChecker.run({ email: next || '' })?.full)
            onChange(next)
          }}
          renderInput={params => (
            <EmailInput
              params={params}
              inputRef={ref}
              name={name}
              label={label}
              error={!!get(errors, name)}
              helperText={get(errors, name)?.message}
              autoComplete={autoComplete}
            />
          )}
        />
      )}
    />
  )
}

EmailField.propTypes = {
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  errors: PropTypes.object,
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  autoComplete: PropTypes.string,
}

export default EmailField
